import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { ContactRelationRow } from "services/api/contacts/types";
import {
  getContactRelationsAPI,
  createContactRelationAPI,
  deleteContactRelationAPI,
} from "services/api/contacts/api";
import { ReactSVG } from "react-svg";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { ContactDropdownTypeEnum } from "ui/components/Dropdown/ContactDropdown/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { ContactRepresentativeStepProps } from "./types";
import { Dialog } from "ui/components/Dialog";

const ContactRepresentativeStep: React.FC<ContactRepresentativeStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [relations, setRelations] = useState<ContactRelationRow[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchRelations = useCallback(async () => {
    if (!contactId) return;
    const result = await getContactRelationsAPI(contactId);
    setRelations(result);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchRelations();
  }, [contactId]);

  const handlePersonChange = async (_e: any, _name: string, item: DropdownOption) => {
    if (!contactId || !item.value) return;

    // Check if person already exists
    const alreadyExists = relations.some(
      (r) => r.personContact?.id === item.value
    );
    if (alreadyExists) return;

    await createContactRelationAPI(contactId, {
      personContactId: item.value,
    });

    fetchRelations();
  };

  const handleDelete = async (relationId: string) => {
    if (!contactId) return;
    await deleteContactRelationAPI(contactId, relationId);
    fetchRelations();
  };

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Predstavnici</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Odaberite osobe koje predstavljaju ovu tvrtku.
          Nakon unosa možete nastaviti na sljedeći korak.
        </p>

        <div className="px-3">
          <div className="mb-4">
            <ContactDropdown
              name="personContactId"
              label="Odaberite osobu"
              placeholder="Pretraži kontakte..."
              value={null}
              onChange={handlePersonChange}
              contactType={ContactDropdownTypeEnum.PERSON}
            />
          </div>

          {relations.length > 0 && (
            <div className="">
              {relations.map((relation:ContactRelationRow) => (
                <div key={relation.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0">
                  <div className="flex items-center gap-3">
                    {relation.personContact?.pictures?.[0]?.url ? (
                      <img
                        src={relation.personContact.pictures[0].url}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                        {relation.personContact?.firstName?.charAt(0)}{relation.personContact?.lastName?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium">
                        {relation.personContact?.firstName} {relation.personContact?.lastName}
                      </div>
                      <div className="flex gap-2 text-xs text-slate-500">
                        {relation.personContact?.jobTitle && <span>{relation.personContact.jobTitle}</span>}
                        {relation.personContact?.department && <span>· {relation.personContact.department}</span>}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setDeleteId(relation.id)} className="p-2 hover:bg-red-100 rounded-full">
                    <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-red-600" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Dialog
        open={!!deleteId}
        title={t("admin.contact.representative.dialog.delete.title") }
        message={t("admin.contact.representative.dialog.delete.message")}
        confirmText={t("admin.contact.representative.dialog.delete.confirmtext")}
        cancelText={t("admin.contact.representative.dialog.delete.canceltext")}
        onConfirm={async () => {
          if (deleteId) await handleDelete(deleteId);
          setDeleteId(null);
        }}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
};

export default memo(ContactRepresentativeStep);
